import { Injectable } from '@angular/core';

import { iif, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { MessageService } from 'primeng/api';

import { ContactsPublicApi } from '../../../../../api/targeting/contacts-public.api';
import { EntityType } from '../../../../../api/targeting/enum/entity.type.enum';
import { HandleTypeEnum } from '../../../../../api/targeting/enum/handle-type.enum';
import { OutletsPublicApi } from '../../../../../api/targeting/outlets-public.api';

import { LocalizationService } from '../../../../../Core/Localization/services/localization.service';
import { messageSeverityEnum } from '../../../../../Common/Misc/enums/message-severity.enum';
import { TargetingRhpContextsService } from '../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';

import { ContactModel } from '../../../../../Common/TargetingShared/model/contact.model';
import { ContactService } from '../../../../../Common/TargetingShared/services/contact-service';
import { HandleModel } from '../../../models/handle.model';
import { OutletModel } from '../../../models/outlet.model';
import { PrivateDataModel } from '../../../models/private-data.model';


@Injectable()
export class AddPrivateDataDialogService {

    constructor(
        public contactsPublicApi: ContactsPublicApi,
        public contactService: ContactService,
        public localizationService: LocalizationService,
        public messageService: MessageService,
        public outletsPublicApi: OutletsPublicApi,
        public targetingRhpContextsService: TargetingRhpContextsService
    ) {

    }

    updatePrivateData(
        systemEntity: ContactModel | OutletModel,
        privateData: PrivateDataModel,
        focusedEntity: ContactModel | OutletModel,
        preferredEmail: HandleModel,
        privateDataDirty: boolean,
        isCachedPreferredEmailPrivate: boolean,
    ) {
        // validation?
        const publicApi = focusedEntity.entityType === EntityType.CONTACT
            ? this.contactsPublicApi
            : this.outletsPublicApi;

        const setPrivateDataRequest = publicApi.setPrivateData(systemEntity.id, privateData.toApiModel());
        const setPreferredEmailRequest = preferredEmail ? publicApi.setPreferredEmail(systemEntity.id, preferredEmail) : of(null);

        const updatePreferred = setPreferredEmailRequest.pipe(
            tap(() => this.updatePreferredEmailComplete(systemEntity, focusedEntity, preferredEmail))
        );
        const updatePrivate = setPrivateDataRequest.pipe(
            tap(() => this.updatePrivateDataComplete(systemEntity, privateData, focusedEntity.entityType, preferredEmail))
        );

        return iif(
            () => privateDataDirty,
            // if there is private data change, update private data first
            updatePrivate.pipe(
                switchMap(() => {
                    // do this after private data updatte if the new preferred email is also a new private email
                    if (preferredEmail) {
                        return updatePreferred;
                    }
                    return of(null);
                })
            ),
            // if no private data change, only call setting preferred email
            updatePreferred.pipe(
                tap(() => {
                    if (preferredEmail.isPrivateData || isCachedPreferredEmailPrivate) {
                        this.updatePrivateDataComplete(systemEntity, privateData, focusedEntity.entityType, preferredEmail);
                    }
                })
            )
        );
    }

    private updatePrivateDataComplete(
        systemEntity: ContactModel | OutletModel,
        privateData: PrivateDataModel,
        entityType: EntityType,
        preferredEmail: HandleModel
    ) {
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Discover.addPrivateDataDialog.sucessSummary'
        });

        if (preferredEmail) {
            privateData.handles.filter(x => x.handleType === HandleTypeEnum.EMAIL).forEach(handle => {
                handle.preferred = false;
                if (handle.value === preferredEmail.value) {
                    handle.preferred = true;
                }
            });
        }
        this.contactService.setPrivateDataAllLayerUpdate({entityType: entityType, entityId: systemEntity.id, privateData: privateData});
    }

    private updatePreferredEmailComplete(systemEntity: ContactModel | OutletModel, focusedEntity: ContactModel | OutletModel, handle: HandleModel) {
        this.messageService.add({
            severity: messageSeverityEnum.success,
            summary: 'Discover.entityProfile.handle.preferred.success.summary',
            detail: this.localizationService.transform('Discover.entityProfile.handle.preferred.success', { email: handle.value })
        });
        this.contactService.setHandlesAllLayerUpdate({
            entityId: systemEntity.id,
            entityType: systemEntity.entityType,
            // since system and private data are dealt separately, filter out private data here
            handles: focusedEntity.handles.filter(x => !x.isPrivateData)
        });
    }
}
